import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { claimVisitorHeaders } from './claim-visitor';
import { Observable } from 'rxjs';
import { PagedResponse } from '../data-transfer-objects/paged-response.dto';
import { GetUserForAdminDto } from '../data-transfer-objects/get-user-for-admin.dto';
import { GetEventToClaimDto } from '../data-transfer-objects/get-event-to-claim.dto';
import { ClaimFunnelRowDto } from '../data-transfer-objects/claim-funnel-row.dto';
import {
  AbuseArchiveDto,
  AbuseCaseDetailDto,
  AbuseCaseDto,
  AbuseOverviewDto,
  AppealDto,
  MemberComplaintDto,
  EmailPreviewDto,
  ThresholdDto,
  BannedPersonaDto,
  PurgeResultDto,
} from '../data-transfer-objects/abuse-case.dto';
import {
  BackfillResultDto,
  ConversationDto,
  EngagementOverviewDto,
  ChatKind,
} from '../data-transfer-objects/engagement.dto';
import {
  MembersBoardDto,
  MembersSnapshotResultDto,
} from '../data-transfer-objects/members.dto';
import {
  TeamChatStateDto,
  TeamMessageSentDto,
} from '../data-transfer-objects/team-chat.dto';
import {
  ProfileFunnelDto,
  ProfileNudgeAllDto,
  ProfileNudgeResultDto,
} from '../data-transfer-objects/profile-funnel.dto';
import {
  EventInsightDetailDto,
  EventsInsightOverviewDto,
} from '../data-transfer-objects/events-insight.dto';
import { EventFormPayload } from '../components/space-claim/event-form';

/*
 * For an endpoint whose whole answer is its status code.
 *
 * Express's `res.sendStatus(200)` sends the word "OK" as text/plain, and
 * HttpClient's default is to parse the body as JSON - so a block that had
 * already disabled the account, revoked its sessions and sent the email came
 * back to the console as "Http failure during parsing", and the admin could
 * not tell a completed action from a failed one. Every caller of one of these
 * endpoints has to say it expects text.
 */
const STATUS_ONLY = { responseType: 'text' as const };

export interface BlockUserOptions {
  email?: { subject: string; text: string };
  reason?: string;
}

@Injectable({
  providedIn: 'root',
})
export class UsersService {
  constructor(private httpClient: HttpClient) {}

  getUsers(params: HttpParams): Observable<PagedResponse<GetUserForAdminDto>> {
    return this.httpClient.get<PagedResponse<GetUserForAdminDto>>(
      `${environment.baseURL}/users`,
      {
        params,
      }
    );
  }
  
  getSpaceToClaim(params: HttpParams): Observable<GetEventToClaimDto> {
    // Retrieve eventID from the current page URL
    const urlParams = new URLSearchParams(window.location.search);
    const eventID = urlParams.get('eventID');
    return this.httpClient.get<GetEventToClaimDto>(
      `${environment.spaceClaimBaseURL}/${eventID}`,
      {
        params,
        // Lets the funnel tell a return visit from a new person. See claim-visitor.
        headers: claimVisitorHeaders(),
      }
    );
  }

  updateSpaceToClaim(eventId: string, updateData: any): Observable<any> {
    return this.httpClient.patch(
      `${environment.spaceClaimBaseURL}/${eventId}`,
      updateData,
      { headers: claimVisitorHeaders() }
    );
  }

  /* Admin-only: behind firebaseAuthMiddleware + adminMiddleware server-side. */
  getProfileFunnel(): Observable<ProfileFunnelDto> {
    return this.httpClient.get<ProfileFunnelDto>(
      `${environment.baseURL}/users/profile-funnel`
    );
  }

  /* Sends one stalled account a reminder. Push if reachable, else email. */
  sendProfileNudge(userId: string): Observable<ProfileNudgeResultDto> {
    return this.httpClient.post<ProfileNudgeResultDto>(
      `${environment.baseURL}/users/${userId}/profile-nudge`,
      {}
    );
  }

  /*
   * Reminds every stalled account that is due one. With dryRun it only counts,
   * which is how the page shows what a click would do before it does it.
   */
  nudgeAllStalled(dryRun: boolean): Observable<ProfileNudgeAllDto> {
    return this.httpClient.post<ProfileNudgeAllDto>(
      `${environment.baseURL}/users/profile-nudge-all`,
      {},
      { params: { dryRun: String(dryRun) } }
    );
  }

  /* Admin-only: behind firebaseAuthMiddleware + adminMiddleware server-side. */
  getClaimFunnel(): Observable<ClaimFunnelRowDto[]> {
    return this.httpClient.get<ClaimFunnelRowDto[]>(
      `${environment.baseURL}/events/claim-funnel`
    );
  }

  unclaimEvent(eventId: string): Observable<any> {
    return this.httpClient.post(
      `${environment.baseURL}/events/${eventId}/unclaim`,
      {}
    );
  }

  /* Clears one event's funnel observations. Counters only - ownership is
     unclaimEvent's job. */
  clearEventFunnel(eventId: string): Observable<{ removed: number }> {
    return this.httpClient.post<{ removed: number }>(
      `${environment.baseURL}/events/${eventId}/clear-funnel`,
      {}
    );
  }

  /* The abuse board. Admin-only, same middleware as the rest of this file. */
  getAbuseOverview(): Observable<AbuseOverviewDto> {
    return this.httpClient.get<AbuseOverviewDto>(
      `${environment.baseURL}/abuse/overview`
    );
  }

  getAbuseCases(): Observable<AbuseCaseDto[]> {
    return this.httpClient.get<AbuseCaseDto[]>(
      `${environment.baseURL}/abuse/cases`
    );
  }

  getAbuseComplaints(): Observable<MemberComplaintDto[]> {
    return this.httpClient.get<MemberComplaintDto[]>(
      `${environment.baseURL}/abuse/complaints`
    );
  }

  getAbuseCase(uid: string): Observable<AbuseCaseDetailDto> {
    return this.httpClient.get<AbuseCaseDetailDto>(
      `${environment.baseURL}/abuse/cases/${uid}`
    );
  }

  dismissAbuseCase(uid: string): Observable<any> {
    return this.httpClient.post(
      `${environment.baseURL}/abuse/cases/${uid}/dismiss`,
      {},
      STATUS_ONLY
    );
  }

  /** Take a post off the feed, whoever wrote it. Its images go with it. */
  deletePost(postId: string): Observable<void> {
    return this.httpClient.delete<void>(`${environment.baseURL}/posts/${postId}`);
  }

  /* Irreversible. Pages through the account's chats: repeat while !done. */
  purgeAbuseChats(uid: string): Observable<PurgeResultDto> {
    return this.httpClient.post<PurgeResultDto>(
      `${environment.baseURL}/abuse/cases/${uid}/purge-chats`,
      {}
    );
  }

  /** What the block email would say, so the console can show it before it sends. */
  getBlockEmailPreview(uid: string): Observable<EmailPreviewDto> {
    return this.httpClient.get<EmailPreviewDto>(
      `${environment.baseURL}/users/${uid}/block-preview`
    );
  }

  /**
   * Block an account. `email` is the reviewed on-hold mail when the admin
   * edited it; `reason` is their note on why, filed with the ban for the next
   * reviewer and never sent to the account holder.
   */
  blockUser(uid: string, options: BlockUserOptions = {}): Observable<any> {
    const body: BlockUserOptions = {};
    if (options.email) body.email = options.email;
    if (options.reason) body.reason = options.reason;
    return this.httpClient.post(`${environment.baseURL}/users/${uid}/block`, body, STATUS_ONLY);
  }

  unblockUser(uid: string): Observable<any> {
    return this.httpClient.post(
      `${environment.baseURL}/users/${uid}/unblock`,
      {},
      STATUS_ONLY
    );
  }

  getBannedPersonas(): Observable<BannedPersonaDto[]> {
    return this.httpClient.get<BannedPersonaDto[]>(
      `${environment.baseURL}/abuse/personas`
    );
  }

  removeBannedPersona(key: string): Observable<any> {
    return this.httpClient.delete(
      `${environment.baseURL}/abuse/personas/${encodeURIComponent(key)}`,
      STATUS_ONLY
    );
  }

  /** One user in the admin list's shape, so the abuse board can open the
      same profile dialog the users list opens. */
  getUserForAdmin(uid: string): Observable<GetUserForAdminDto> {
    return this.httpClient.get<GetUserForAdminDto>(
      `${environment.baseURL}/users/${uid}`
    );
  }

  getAppeals(): Observable<AppealDto[]> {
    return this.httpClient.get<AppealDto[]>(
      `${environment.baseURL}/abuse/appeals`
    );
  }

  /** What the outcome email would say, so the console can show it before it sends. */
  getAppealDecisionPreview(
    id: string,
    decision: 'restore' | 'uphold'
  ): Observable<EmailPreviewDto> {
    return this.httpClient.get<EmailPreviewDto>(
      `${environment.baseURL}/abuse/appeals/${id}/preview/${decision}`
    );
  }

  /** Decides the appeal, restores the account when that is the decision, and
      emails the person - one call, because the email is what gets skipped. */
  decideAppeal(
    id: string,
    decision: 'restore' | 'uphold',
    email?: { subject: string; text: string }
  ): Observable<AppealDto> {
    return this.httpClient.post<AppealDto>(
      `${environment.baseURL}/abuse/appeals/${id}/${decision}`,
      email ? { email } : {}
    );
  }

  getGuardThresholds(): Observable<ThresholdDto[]> {
    return this.httpClient.get<ThresholdDto[]>(
      `${environment.baseURL}/abuse/thresholds`
    );
  }

  /** Only the values being changed need sending; the rest are left alone. */
  updateGuardThresholds(values: Record<string, number>): Observable<ThresholdDto[]> {
    return this.httpClient.put<ThresholdDto[]>(
      `${environment.baseURL}/abuse/thresholds`,
      values
    );
  }

  getAbuseArchives(): Observable<AbuseArchiveDto[]> {
    return this.httpClient.get<AbuseArchiveDto[]>(
      `${environment.baseURL}/abuse/archives`
    );
  }

  /* Chat engagement. Metadata only - the API never returns message bodies. */
  /* kind narrows to peer chats or lobby rooms; left out, both are counted. */
  getEngagementOverview(days = 28, kind?: ChatKind): Observable<EngagementOverviewDto> {
    return this.httpClient.get<EngagementOverviewDto>(
      `${environment.baseURL}/engagement/overview?days=${days}${kind ? '&kind=' + kind : ''}`
    );
  }

  getConversations(days = 28, kind?: ChatKind): Observable<ConversationDto[]> {
    return this.httpClient.get<ConversationDto[]>(
      `${environment.baseURL}/engagement/conversations?days=${days}${kind ? '&kind=' + kind : ''}`
    );
  }

  /* Paged: repeat while !done. */
  backfillEngagement(): Observable<BackfillResultDto> {
    return this.httpClient.post<BackfillResultDto>(
      `${environment.baseURL}/engagement/backfill`,
      {}
    );
  }

  /* The Members board: every account scored from the nightly snapshot. */
  getMembersBoard(): Observable<MembersBoardDto> {
    return this.httpClient.get<MembersBoardDto>(`${environment.baseURL}/members`);
  }

  /* Whether a conversation between this member and the sender already
     exists, and who spoke. The team account when fromUid is omitted. */
  getTeamChatState(userId: string, fromUid?: string): Observable<TeamChatStateDto> {
    return this.httpClient.get<TeamChatStateDto>(
      `${environment.baseURL}/users/${userId}/team-chat`,
      fromUid ? { params: { from: fromUid } } : {}
    );
  }

  /* Send one message as fromUid (an account from the senders list). Not undoable. */
  sendTeamMessage(
    userId: string,
    text: string,
    opener: string | null,
    fromUid: string
  ): Observable<TeamMessageSentDto> {
    return this.httpClient.post<TeamMessageSentDto>(
      `${environment.baseURL}/users/${userId}/team-message`,
      { text, opener, fromUid }
    );
  }

  /* Rebuild the snapshot now. Takes a few seconds; the board reloads after. */
  recomputeMembers(): Observable<MembersSnapshotResultDto> {
    return this.httpClient.post<MembersSnapshotResultDto>(
      `${environment.baseURL}/members/recompute`,
      {}
    );
  }

  /* The events board: every space with its organizer, members and lobby
     activity. Admin-only; lobby numbers are counts, never message bodies. */
  getEventsInsight(days = 28): Observable<EventsInsightOverviewDto> {
    return this.httpClient.get<EventsInsightOverviewDto>(
      `${environment.baseURL}/events/insights?days=${days}`
    );
  }

  getEventInsight(eventId: string, days = 28): Observable<EventInsightDetailDto> {
    return this.httpClient.get<EventInsightDetailDto>(
      `${environment.baseURL}/events/insights/${encodeURIComponent(eventId)}?days=${days}`
    );
  }

  /* The stored logo as a data URL, so the cropper can re-open it. Goes through
     our API because the storage bucket sends no CORS headers, and an image the
     canvas cannot read back cannot be re-cropped. */
  getEventLogoImage(eventId: string): Observable<{ dataUrl: string }> {
    return this.httpClient.get<{ dataUrl: string }>(
      `${environment.baseURL}/events/${encodeURIComponent(eventId)}/logo-image`
    );
  }

  /* Edit somebody else's space. Same fields and rules as the claim wizard;
     ownership is not settable here. Answers 204 with no body. */
  updateEventDetails(eventId: string, update: EventFormPayload): Observable<void> {
    return this.httpClient.patch<void>(
      `${environment.baseURL}/events/${encodeURIComponent(eventId)}`,
      update
    );
  }
}
